import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch } from "../../hooks/useStore.ts";
import { useForm } from "react-hook-form";
import {
  Mail,
  ArrowLeft,
  Building,
  CheckCircle,
  Shield,
  Clock,
  RefreshCw,
  Lock,
} from "lucide-react";
import { forgotPassword, clearError } from "../../store/slices/authSlice";
import Button from "../../components/UI/Button.tsx";
import { toast } from "react-toastify";
import { AuthHeroSection } from "../../components/auth/AuthHeroSection.tsx";
import { ForgotSuccessHero } from "../../components/auth/ForgotSuccessHero.tsx";
import { forgotSchema } from "../../schemavalidations/index.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { translateValidation } from "../../utils/translateValidation.ts";

interface ForgotPasswordForm {
  email: string;
}

const ForgotPasswordPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation("auth");

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordForm>({
    resolver: yupResolver(forgotSchema),
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setLoading(true);
    try {
      await dispatch(forgotPassword(data.email)).unwrap();
      setIsSubmitted(true);
      toast.success(t("forgotPassword.successSent"));
    } catch (error) {
      toast.error(t("forgotPassword.failedToSend"));
    } finally {
      setLoading(false);
      dispatch(clearError());
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex">
        {/* Left Side - Success Hero Section */}
        <ForgotSuccessHero
          brandIcon={CheckCircle}
          brandTitle={t("forgotPassword.success.brandTitle")}
          mainTitle={t("forgotPassword.success.mainTitle")}
          highlightedTitle={t("forgotPassword.success.highlightedTitle")}
          description={t("forgotPassword.success.description")}
          steps={[
            {
              number: 1,
              title: t("forgotPassword.success.step1Title"),
              description: t("forgotPassword.success.step1Desc"),
            },
            {
              number: 2,
              title: t("forgotPassword.success.step2Title"),
              description: t("forgotPassword.success.step2Desc"),
            },
            {
              number: 3,
              title: t("forgotPassword.success.step3Title"),
              description: t("forgotPassword.success.step3Desc"),
            },
          ]}
        />

        {/* Right Side - Success Message */}
        <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            {/* Mobile Success Icon */}
            <div className="lg:hidden text-center mb-8">
              <div className="mx-auto h-16 w-16 bg-[#00A862] rounded-xl flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-2xl font-medium tracking-tight text-[#0B0F0E]">
                {t("forgotPassword.success.emailSent")}
              </h1>
            </div>

            <div className="bg-white rounded-2xl border border-[rgba(11,15,14,0.08)] overflow-hidden shadow-sm">
              <div className="p-8 border-b border-[rgba(11,15,14,0.06)] text-center">
                <div className="mx-auto h-16 w-16 bg-[#00A862] rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-medium tracking-tight text-[#0B0F0E] mb-2">
                  {t("forgotPassword.success.checkYourEmail")}
                </h2>
                <p className="text-[#8A9590]">
                  {t("forgotPassword.success.sentInstructions")}
                </p>
                <p className="font-medium text-[#0B0F0E] mt-1">
                  {getValues("email")}
                </p>
              </div>

              <div className="p-8 space-y-6">
                <div className="bg-[#e6f7ef] border border-[#84d9b2] rounded-lg p-4">
                  <div className="flex items-start">
                    <Mail className="h-5 w-5 text-[#00A862] mt-0.5 mr-3 shrink-0" />
                    <div>
                      <h3 className="font-medium text-[#004b2c] text-sm mb-1">
                        {t("forgotPassword.success.emailNotReceived")}
                      </h3>
                      <p className="text-[#007a49] text-xs">
                        {t("forgotPassword.success.checkSpam")}
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="w-full py-3 transition-all duration-200"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t("forgotPassword.success.tryAgain")}
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm font-medium text-[#C97A3C] hover:text-[#B86B30] transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t("forgotPassword.backToSignIn")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex">
      {/* Left Side - Hero Section */}
      <AuthHeroSection
        brandIcon={Lock}
        brandTitle={t("forgotPassword.hero.brandTitle")}
        mainTitle={t("forgotPassword.hero.mainTitle")}
        highlightedTitle={t("forgotPassword.hero.highlightedTitle")}
        description={t("forgotPassword.hero.description")}
        features={[
          {
            icon: Shield,
            title: t("forgotPassword.hero.feature1Title"),
            description: t("forgotPassword.hero.feature1Desc"),
          },
          {
            icon: Clock,
            title: t("forgotPassword.hero.feature2Title"),
            description: t("forgotPassword.hero.feature2Desc"),
          },
          {
            icon: Mail,
            title: t("forgotPassword.hero.feature3Title"),
            description: t("forgotPassword.hero.feature3Desc"),
          },
        ]}
      />

      {/* Right Side - Reset Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="mx-auto flex items-center justify-center gap-3 mb-4">
              <svg
                width="36"
                height="36"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect
                  x="22"
                  y="38"
                  width="2"
                  height="24"
                  fill="#C97A3C"
                  opacity="0.5"
                />
                <rect
                  x="27"
                  y="34"
                  width="2"
                  height="32"
                  fill="#C97A3C"
                  opacity="0.6"
                />
                <rect
                  x="32"
                  y="30"
                  width="3"
                  height="40"
                  fill="#C97A3C"
                  opacity="0.7"
                />
                <rect
                  x="38"
                  y="24"
                  width="3"
                  height="52"
                  fill="#C97A3C"
                  opacity="0.85"
                />
                <rect x="44" y="18" width="4" height="64" fill="#E89D5C" />
                <rect
                  x="51"
                  y="24"
                  width="3"
                  height="52"
                  fill="#C97A3C"
                  opacity="0.85"
                />
                <rect
                  x="57"
                  y="30"
                  width="3"
                  height="40"
                  fill="#C97A3C"
                  opacity="0.7"
                />
                <rect
                  x="63"
                  y="34"
                  width="2"
                  height="32"
                  fill="#C97A3C"
                  opacity="0.6"
                />
                <rect
                  x="68"
                  y="38"
                  width="2"
                  height="24"
                  fill="#C97A3C"
                  opacity="0.5"
                />
              </svg>
              <h1 className="text-2xl font-medium tracking-tight text-[#0B0F0E]">
                LiveCargo<span className="text-[#C97A3C]">.</span>
              </h1>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[rgba(11,15,14,0.08)] overflow-hidden shadow-sm">
            <div className="p-8 border-b border-[rgba(11,15,14,0.06)]">
              <h2 className="text-2xl font-medium tracking-tight text-[#0B0F0E] mb-2">
                {t("forgotPassword.resetPassword")}
              </h2>
              <p className="text-[#8A9590]">
                {t("forgotPassword.resetDescription")}
              </p>
            </div>

            <form className="p-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-[#8A9590] mb-2">
                  {t("forgotPassword.emailAddress")}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#8A9590]" />
                  <input
                    {...register("email")}
                    type="email"
                    placeholder={t("forgotPassword.emailPlaceholder")}
                    className={`w-full pl-12 pr-4 py-3.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C97A3C] focus:border-transparent transition-all duration-200 bg-[#FFFFFF] focus:bg-white text-[#0B0F0E] placeholder:text-[#8A9590] ${
                      errors.email
                        ? "border-[#E24B4A]"
                        : "border-[rgba(11,15,14,0.15)]"
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-[#E24B4A] flex items-center">
                    <span className="w-1 h-1 bg-[#E24B4A] rounded-full mr-2"></span>
                    {translateValidation(errors.email.message || "")}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-[#C97A3C] hover:bg-[#B86B30] text-white py-3.5 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium shadow-sm"
                isLoading={loading}
                disabled={loading}
              >
                {loading
                  ? t("forgotPassword.sending")
                  : t("forgotPassword.sendResetInstructions")}
              </Button>

              <div className="text-center pt-4">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm font-medium text-[#C97A3C] hover:text-[#B86B30] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t("forgotPassword.backToSignIn")}
                </Link>
              </div>
            </form>
          </div>

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-[#F5F4F0] border border-[rgba(11,15,14,0.08)] rounded-lg">
            <div className="flex items-start">
              <Shield className="h-5 w-5 text-[#C97A3C] mt-0.5 mr-3 shrink-0" />
              <div>
                <h3 className="font-medium text-[#0B0F0E] text-sm mb-1">
                  {t("forgotPassword.securityNotice")}
                </h3>
                <p className="text-[#8A9590] text-xs">
                  {t("forgotPassword.securityNoticeText")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
